import { Injectable } from '@angular/core';
import { File, FileEntry, IFile as CordovaFile, Metadata } from '@ionic-native/file';
import { UUID } from 'angular2-uuid';
import { FileReaderHelper } from './file-reader.helper';
import { FileHelper } from './file.helper';

export declare type CallBackType = (chunk: Blob) => Promise<any>;

export interface CordovaFileEntryInfo {
    filename: string;
    directoryPath: string;
}

@Injectable()
export class CordovaFileHelper {

    static readonly CHUNK_SIZE = 1024 * 1024 * 10;

    /**
     * This function accepts an instance of FileEntry which represents the
     * target file where the data will be written.
     *
     * If the size of the data is larger than the default CHUNK_SIZE then it
     * will be written in chunks.
     */
    static async write(fileEntry: FileEntry, blob: Blob, chunkSize: number = CordovaFileHelper.CHUNK_SIZE): Promise<any> {

        const meta = await CordovaFileHelper.getMetaData(fileEntry);

        if (meta.size <= chunkSize) {
            await CordovaFileHelper.writeChunk(fileEntry, blob, 0, false);
            await CordovaFileHelper.verifyWrite(fileEntry, blob);
            return;
        }

        for (let i = 0; i < meta.size; i += chunkSize) {
            await CordovaFileHelper.writeChunk(fileEntry, blob, i);
        }

        await CordovaFileHelper.verifyWrite(fileEntry, blob);
    }

    static async verifyWrite(
        fileEntry: FileEntry,
        blob: Blob
    ): Promise<boolean> {
        const metadata = await CordovaFileHelper.getMetaData(fileEntry);
        if (metadata.size !== blob.size) {
            return Promise.reject(
                Error(`blob.size does not match expected size of ${metadata.size}`)
            );
        }
    }

    static async writeChunk(
        fileEntry: FileEntry,
        blob: Blob,
        chunkIndex: number = 0,
        isAppend: boolean = true,
        chunkSize: number = CordovaFileHelper.CHUNK_SIZE
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            fileEntry.createWriter(
                fileWriter => {

                    fileWriter.onwriteend = resolve;
                    fileWriter.onerror = reject;
                    fileWriter.onabort = reject;

                    try {
                        if (isAppend) {
                            fileWriter.seek(fileWriter.length);
                        }
                        const start = chunkIndex * chunkSize;
                        const end = Math.min(
                            (chunkIndex + 1) * chunkSize,
                            blob.size
                        );
                        fileWriter.write(blob.slice(start, end));
                    } catch (e) {
                        reject(e);
                    }
                },
                reject
            );
        });
    }

    /**
     * Returns a single chunk of data from an IFile.
     *
     * This method looks a little over complicated. But this approach is
     * required for cordova files because simply slicing them won't work since
     * the IFile does not actually contain the data.
     *
     * https://cordova.apache.org/docs/en/3.1.0/cordova/file/fileobj/fileobj.html#slice
     *
     * @param file {IFile}
     * @param index {number} the index of the chunk. negative numbers start from the end
     * @param size {number} the size of the chunk in bytes
     * @return {Promise<Blob>}
     */
    static async readChunk(
        file: CordovaFile,
        index: number = 0,
        size: number = CordovaFileHelper.CHUNK_SIZE
    ): Promise<Blob> {

        return new Promise<Blob>((resolve, reject) => {
            const reader = FileReaderHelper.getFileReader();

            reader.onloadend = () => {
                resolve(new Blob([reader.result]));
            };

            reader.onerror = reject;
            reader.onabort = reject;

            reader.readAsArrayBuffer(
                file.slice(index * size, (index + 1) * size)
            );
        });
    }

    static async getMetaData(fileEntry: FileEntry): Promise<Metadata> {
        return new Promise<Metadata>((resolve, reject) => {
            fileEntry.getMetadata(resolve, reject);
        });
    }

    static async getFile(fileEntry: FileEntry): Promise<CordovaFile> {
        return new Promise<CordovaFile>((resolve, reject) => {
            fileEntry.file(resolve, reject);
        });
    }

    static async remove(fileEntry: FileEntry): Promise<any> {
        return new Promise((resolve, reject) => {
            fileEntry.remove(resolve, reject);
        });
    }

    static cordovaFileToBlob(file: CordovaFile): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const reader = FileReaderHelper.getFileReader();

            reader.onloadend = () => {
                const blob = new Blob([reader.result]);
                // tslint:disable-next-line:no-string-literal
                blob['name'] = file.name;
                resolve(blob);
            };

            reader.onerror = e => {
                reject(e);
            };

            reader.readAsArrayBuffer(file);
        });
    }

    static extractFilePathParts(path: string = ''): CordovaFileEntryInfo {
        const directoryPath = path.substring(0, path.lastIndexOf('/') + 1);
        const filename = path.substring(path.lastIndexOf('/') + 1);
        const standardizedDirectoryPath = directoryPath.replace(/http.*:\d+/, 'file://');

        return {
            filename,
            directoryPath: standardizedDirectoryPath
        };
    }

    static async pathToObjectUrl(path: string, nativeFileService: File): Promise<string> {
        const blob = await CordovaFileHelper.pathToBlob(
            path,
            nativeFileService
        );
        return URL.createObjectURL(blob);
    }

    static async pathToBlob(path: string, nativeFileService: File): Promise<Blob> {
        const iFile = await CordovaFileHelper.pathToCordovaFile(
            path,
            nativeFileService
        );
        return CordovaFileHelper.cordovaFileToBlob(iFile);
    }

    static async pathToCordovaFile(path: string, nativeFileService: File): Promise<CordovaFile> {

        const parts = CordovaFileHelper.extractFilePathParts(path);

        const directoryEntry = await nativeFileService.resolveDirectoryUrl(
            parts.directoryPath
        );

        const fileEntry = await nativeFileService.getFile(
            directoryEntry,
            parts.filename,
            {create: false, exclusive: false}
        );

        return CordovaFileHelper.getFile(fileEntry);
    }

    static async blobToTempFileEntry(blob: Blob, nativeFileService: File): Promise<FileEntry> {

        const name = blob['name'] || UUID.UUID();

        const directoryEntry = await nativeFileService.resolveDirectoryUrl(
            nativeFileService.dataDirectory
        );

        return nativeFileService.getFile(
            directoryEntry,
            name,
            {
                create: true,
                exclusive: false
            }
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/file-system/cordova-file.helper.ts